import { useQuery } from '@tanstack/react-query'
import { CircleAlert, ShieldCheck } from 'lucide-react'
import { Link, useLocation } from 'react-router'

import { fetchBilling } from '@/api/billing'
import type { MyOrg, VerificationStatus } from '@/api/types'
import { Alert, AlertAction, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { formatRupees } from '@/lib/format'

/**
 * Across the app, whatever stops (or will soon stop) phone calls, one thing at a time, as a warning: first a business
 * staff haven't approved (Verify your business, the business page), then a balance below the org's low-balance alert,
 * which says so plainly once it can't cover a minute. Owners get the button to fix it; everyone else is told who can.
 * Simulations and browser calls still work, so it only explains, never blocks.
 */
export const LiveCallsBanner = ({ org }: { org: MyOrg }) => {
  const billing = useQuery({ queryKey: ['billing', org.id], queryFn: () => fetchBilling(org.id), staleTime: 60_000 })
  const canManage = org.permissions.includes('billing.manage')
  const billingPath = `/o/${org.slug}/settings/billing`
  const { status, note } = org.verification
  const { pathname } = useLocation()

  if (status !== 'verified') {
    // On the business page itself the page says it, so the banner doesn't.
    if (pathname.endsWith('/business')) return null
    return <VerifyBanner orgSlug={org.slug} status={status} note={note} canManage={canManage} />
  }

  const account = billing.data?.account
  // An alert set under a minute's rate still warns once live calls can't start.
  if (!account || account.balancePaise >= Math.max(account.lowBalancePaise, account.ratePaisePerMinute)) return null
  const cantCall = account.balancePaise < account.ratePaisePerMinute
  return (
    <Alert variant="warning" className="mb-6 print:hidden">
      <CircleAlert />
      <AlertTitle>{cantCall ? 'Live calls need credit' : 'Your balance is low'}</AlertTitle>
      <AlertDescription>
        {cantCall
          ? `Your balance is ${formatRupees(account.balancePaise)}. Live calls start once it covers a minute (${formatRupees(account.ratePaisePerMinute)}); test calls work meanwhile.`
          : `Your balance is ${formatRupees(account.balancePaise)}, below your alert of ${formatRupees(account.lowBalancePaise)}. Top up so live calls keep going.`}
        {!canManage && ' Ask an owner to top up.'}
      </AlertDescription>
      {canManage && (
        <AlertAction>
          <Button asChild size="sm">
            <Link to={billingPath}>Top up</Link>
          </Button>
        </AlertAction>
      )}
    </Alert>
  )
}

const VERIFY_DESCRIPTION: Record<'unverified' | 'pending', string> = {
  unverified:
    'Tell RowCall about your business (type, PAN, GSTIN, your store) and it checks them and gives you a number to call from.',
  pending: 'RowCall is checking your business details; you’ll get an email when it’s done.',
}

/** A business staff haven't approved yet: what to do (or that RowCall is checking), and the way to the business page. */
const VerifyBanner = ({
  orgSlug,
  status,
  note,
  canManage,
}: {
  orgSlug: string
  status: Exclude<VerificationStatus, 'verified'>
  note: string | null
  canManage: boolean
}) => {
  const isPending = status === 'pending'
  const description =
    status === 'rejected'
      ? `RowCall needs some changes${note ? `: ${note}` : '.'} Fix them and send them again.`
      : VERIFY_DESCRIPTION[status]
  return (
    <Alert variant="warning" className="mb-6 print:hidden">
      <ShieldCheck />
      <AlertTitle>
        {isPending ? 'RowCall is checking your business' : 'Verify your business to make phone calls'}
      </AlertTitle>
      <AlertDescription>
        {`${description} Simulations and browser calls work meanwhile.`}
        {!canManage && !isPending && ' Ask an owner to send them.'}
      </AlertDescription>
      <AlertAction>
        <Button asChild size="sm" variant={isPending ? 'outline' : 'default'}>
          <Link to={`/o/${orgSlug}/business`}>{isPending ? 'See details' : 'Verify your business'}</Link>
        </Button>
      </AlertAction>
    </Alert>
  )
}
